import * as React from 'react';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import './contentBusinessCaseModal.scss';
import { ModalNav } from '../../ModalNav/modalNav';
import { ConfigureMainWorkflow } from './configureMainWorkflow';
import { Metadata } from '../../../types';
import { FormUpdater } from '../../../types/Field';
import { WorkflowSavePayload } from '../../../types/ConfigureWorkflowTypes';
import { useWorkflowModal } from './useWorkflowModal';
import { useActions } from '../metadataFormActions';
import { useSelector } from '../../../utils/useSelector';
import { getItemStatusId } from '../../../utils';

type ContentBusinessCaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data: Partial<Metadata>;
  updater?: FormUpdater;
};

enum ContentBusinessCasePage {
  ContentOpportunity = 'ContentOpportunity',
  ConfigureWorkflow = 'ConfigureWorkflow'
}

const businessCasePages = [
  {
    id: ContentBusinessCasePage.ContentOpportunity,
    label: 'Content Opportunity',
    Icon: null
  },
  {
    id: ContentBusinessCasePage.ConfigureWorkflow,
    label: 'Configure Workflow',
    Icon: null
  }
];

export const ContentBusinessCaseModal = ({
  isOpen,
  onClose,
  data,
  updater
}: ContentBusinessCaseModalProps) => {
  const { updateConfigureWorkflow } = useActions();
  const empCd = useSelector(s => s.app.user.employeeId);

  const {
    workflowState,
    setWorkflowState,
    onWorkflowStateChange,
    isSaving,
    isSaveDisabled,
    setIsSaveDisabled,
    errorMessage,
    setHasChanges,
    setIsModalDisabled,
    handleClose,
    ConfirmModal,
    wrapSave,
    reset
  } = useWorkflowModal({ onClose });

  const effectiveWorkflowState = React.useMemo(
    () =>
      workflowState || {
        configureWorkflowSkipIds: data?.props?.configureWorkflowSkipIds,
        configureWorkflowType: data?.props?.configureWorkflowType,
        requestCreativeHelp: data?.props?.requestCreativeHelp || false
      },
    [data, workflowState]
  );

  const [activePage, setActivePage] = React.useState<ContentBusinessCasePage>(
    ContentBusinessCasePage.ContentOpportunity
  );

  React.useEffect(() => {
    if (isOpen) {
      setActivePage(ContentBusinessCasePage.ContentOpportunity);
      setWorkflowState({
        configureWorkflowSkipIds: data?.props?.configureWorkflowSkipIds,
        configureWorkflowType: data?.props?.configureWorkflowType,
        requestCreativeHelp: data?.props?.requestCreativeHelp || false
      });
      return;
    }

    reset();
  }, [data, isOpen, reset, setWorkflowState]);

  const handleMainWorkflowSave = React.useCallback(
    async ({
      requestCreativeHelp,
      configureWorkflowType,
      configureWorkflowSkipIds
    }: WorkflowSavePayload) => {
      const newData: Partial<Metadata> = {
        id: data.id,
        labels: data.labels,
        props: {
          refId: data.props.refId,
          requestCreativeHelp,
          configureWorkflowByEmpCd: empCd,
          configureWorkflowType,
          configureWorkflowSkipIds
        }
      };
      if (!!data.props.itemId) {
        newData.props.itemId = data.props.itemId;
      }
      await updateConfigureWorkflow(newData, false, getItemStatusId(data));
    },
    [data, empCd, updateConfigureWorkflow]
  );

  const onSave = wrapSave(
    async () => {
      await handleMainWorkflowSave(effectiveWorkflowState);
      onClose();
    },
    {
      errorMessage: 'Failed to save workflow configuration. Please try again.'
    }
  );

  const onContinue = React.useCallback(() => {
    //To do: Save the Content Opportunity Page data here
    setActivePage(ContentBusinessCasePage.ConfigureWorkflow);
  }, []);

  const isContentOpportunityPage =
    activePage == ContentBusinessCasePage.ContentOpportunity;

  return (
    <>
      <Modal
        header="Content Business Case"
        id="contentBusinessCaseModal"
        onClose={onClose}
        footer={
          <Buttons>
            <Secondary
              label="Cancel"
              onClick={isContentOpportunityPage ? onClose : handleClose}
            />
            <Primary
              label={isContentOpportunityPage ? 'Continue' : 'Save'}
              onClick={isContentOpportunityPage ? onContinue : onSave}
              disabled={!isContentOpportunityPage && isSaveDisabled}
            />
          </Buttons>
        }
      >
        <div className="content-business-case-modal-nav">
          <ModalNav
            selectedPage={activePage}
            pages={businessCasePages}
            goToPage={p => setActivePage(p.id as ContentBusinessCasePage)}
            prevPage={() =>
              setActivePage(ContentBusinessCasePage.ContentOpportunity)
            }
          />
        </div>
        {isContentOpportunityPage ? (
          <div className="content-business-case-modal-estimated-dpv">
            <div className="content-business-case-modal-section-title">
              Estimated DPV
            </div>
            {
              //To do: Placeholder for estimated DPV content
            }
          </div>
        ) : (
          <ConfigureMainWorkflow
            onClose={onClose}
            data={data}
            onSave={handleMainWorkflowSave}
            workflowState={effectiveWorkflowState}
            onWorkflowStateChange={onWorkflowStateChange}
            onSaveDisabledChange={setIsSaveDisabled}
            errorMessage={errorMessage}
            onHasChangesChange={setHasChanges}
            onIsModalDisabledChange={setIsModalDisabled}
            isSaving={isSaving}
          />
        )}
      </Modal>
      <ConfirmModal />
    </>
  );
};
